import { Injectable } from '@angular/core';
import { Entry } from 'functions/src/models/Entry';
import { BehaviorSubject } from 'rxjs';

import { EntryService } from 'src/services/entry/entry.service';
import { TagTreeMapper } from 'src/services/tag-tree/tag-tree.service';
import { Period, Tag, TagPeriod } from 'src/services/tag/Tag';
import { TagService } from 'src/services/tag/tag.service';
import { getTopLevelTag } from 'src/utils/getTopLevelTag';
import { isParent } from 'src/utils/isParent';
import { isWithinCurrentPeriod } from 'src/utils/isWithinCurrentPeriod';

@Injectable({
  providedIn: 'root',
})
export class PeriodService {
  tagPeriods: TagPeriod[] = [];

  private tags: Tag[] = [];
  private entries: Entry[] = [];

  private _current: Period = {
    year: new Date().getFullYear(),
    month: new Date().getMonth() + 1,
  };

  public periodSubject = new BehaviorSubject<Period>(this.current);

  set current(val: Period) {
    this._current = val;
  }

  get current(): Period {
    return this._current;
  }

  constructor(
    private tagService: TagService,
    private entryService: EntryService
  ) {
    this.tagService.tags.subscribe((tags) => {
      this.tags = tags;
    });

    this.entryService.entries.subscribe((entries) => {
      this.entries = entries;
    });
  }

  public getActualExpenditureForPeriod(
    tagId: string,
    period: Period = this.current
  ) {
    return this.calculateActualForPeriod(
      tagId,
      period,
      TagTreeMapper.calculateActualExpenditure
    );
  }

  public getActualIncomeForPeriod(
    tagId: string,
    period: Period = this.current
  ) {
    return this.calculateActualForPeriod(
      tagId,
      period,
      TagTreeMapper.calculateActualIncome
    );
  }

  public gerRunningBalanceForPeriod(
    tagId: string,
    period: Period = this.current
  ) {
    return this.calculateActualForPeriod(
      tagId,
      period,
      TagTreeMapper.calculateRunnningBalance
    );
  }

  calculateActualForPeriod(
    tagId: string,
    period: Period,
    calculateFunction: Function
  ) {
    const tag = this.tags.find((t) => t.id === tagId);
    if (tag) {
      const targetTagIds = this.tags
        .filter(
          (t) =>
            isParent(tag.fullPath, t.fullPath) || tag.fullPath === t.fullPath
        )
        .map((t) => t.id);

      const entries = this.entries.filter((e) =>
        isWithinCurrentPeriod(e, period)
      );
      return calculateFunction(entries, targetTagIds, tag);
    } else {
      return 0;
    }
  }

  next() {
    const isDecember = this.current.month === 12;
    const year = isDecember ? this.current.year + 1 : this.current.year;
    const month = isDecember ? 1 : this.current.month + 1;
    this.current = { year, month };
    this.periodSubject.next(this.current);
  }

  previous() {
    const isJanuary = this.current.month === 1;
    const year = isJanuary ? this.current.year - 1 : this.current.year;
    const month = isJanuary ? 12 : this.current.month - 1;
    this.current = { year, month };
    this.periodSubject.next(this.current);
  }

  isTodayWithinCurrent() {
    const today = new Date();
    const todayYear = today.getFullYear();
    const todayMonth = today.getMonth() + 1;
    return this.current.month === todayMonth && this.current.year === todayYear;
  }

  public getDefaultDateForEntry(
    tag: Tag | null | undefined,
    tags: Tag[]
  ): Date {
    if (!tag) {
      return new Date();
    } else {
      if (!getTopLevelTag(tag, tags)?.isPeriodic) {
        return new Date();
      } else {
        if (this.isTodayWithinCurrent()) {
          return new Date();
        } else {
          const newDate = new Date();
          newDate.setFullYear(this.current.year);
          newDate.setMonth(this.current.month - 1);
          newDate.setDate(1);

          return newDate;
        }
      }
    }
  }
}
